import React, { useEffect, useState } from "react";
import Input from "../../../components/FormComponents/Input";
import Textarea from "../../../components/FormComponents/Textarea";
import Loader from "../../../Layouts/Loader/Loader";
import ErrorMessage from "../../../components/FormComponents/ErrorMessage";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import { useTemplateContext } from "../../../context/TemplateContext";
import { toastSuccess } from "../../../utils/toastUtils";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import ExcelTemplateFileUpload from "./ExcelTemplateFileUpload";
import ExcelTemplateFieldsManager from "./ExcelTemplateFieldsManager";
import useDirtyChecker from "../../../hooks/useDirtyChecker";

const ExcelTemplateEditor = ({ file, templateData, setTemplateData }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { isDirty, markClean } = useDirtyChecker(templateData);

  const handleChange = (e) => {
    setTemplateData({ ...templateData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const payload = {
        ...templateData,
        name: file.name // 🔁 ensure file name is consistent
      };
      const { message } = await axiosInstance.patch(`/exceldoc/${file.id}`, payload);
      toastSuccess(message || "Excel Template saved successfully.");
      markClean();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d_flex flex_column">
      <GenericActionBar
        path={
          <div>
            <em>Excel Templates</em> → <span>{file.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSave
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />

      <div className="template_form_outer">
        <div className="builder_form">
          <Input
            label="Template Name"
            name="template_name"
            value={templateData.template_name || ""}
            onChange={handleChange}
          />

          <ExcelTemplateFileUpload
            templateId={file.id}
            templateName={templateData.template_name}
            templateFileId={templateData.template_id || ""}
            onFileUploadSuccess={(id) => setTemplateData((prev) => ({ ...prev, template_id: id }))}
          />

          <ExcelTemplateFieldsManager
            fieldsArr={templateData.excel_variables || []}
            setFieldsArr={(newFields) => setTemplateData((prev) => ({ ...prev, excel_variables: newFields }))}
          />
          <Textarea
            label="Custom JS"
            name="custom_js"
            value={templateData.custom_js || ""}
            onChange={handleChange}
            style={{ height: "300px" }}
          />
        </div>
      </div>
    </div>
  );
};

const ExcelTemplateViewer = ({ file }) => {
  const { templateData, setTemplateData } = useTemplateContext();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchTemplate = async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(`/exceldoc/${file.id}`);
      setTemplateData(data);
    } catch (err) {
      setError("Failed to load Excel template.");
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  if (isLoading) return <Loader />;
  if (error) {
    return (
      <div className="d_flex justify_center align_center h_100">
        <ErrorMessage>{error}</ErrorMessage>
      </div>
    );
  }

  if (!templateData) return null;

  return <ExcelTemplateEditor file={file} templateData={templateData} setTemplateData={setTemplateData} />;
};

export default ExcelTemplateViewer;
